import type { WpsEditableSnapshot } from '../WpsPreviewPage'

/**
 * Pure-function diff between two `WpsEditableSnapshot`s. Slice C.3 of
 * wps-product-parity-fixes — the WPS-side parallel of
 * `buildPqrPreviewDiff`. Returns the same shape so the new
 * `WpsDiffConfirmDialog` can re-use the PQR table renderer with a
 * minimal copy-paste.
 *
 * The diff is intentionally shallow:
 *   - Identifiers: per-field rows for WPS No., Rev., Application,
 *     Equipment.
 *   - Signatures (approvals): one row per `id` that changed, comparing
 *     "Name (Title)" + Date columns.
 *   - Notes: one row per note `id` that changed, comparing the body
 *     verbatim.
 *
 * Identical fields are dropped (no row, no group). The whole object
 * carries a `hasChanges` flag so consumers can render a
 * "No changes detected" placeholder without re-summing.
 */
export type WpsPreviewDiffGroupKey = 'Identifiers' | 'Signatures' | 'Notes'

export interface WpsPreviewDiffRow {
  field: string
  before: string
  after: string
}

export interface WpsPreviewDiffGroup {
  group: WpsPreviewDiffGroupKey
  rows: WpsPreviewDiffRow[]
}

export interface WpsPreviewDiff {
  groups: WpsPreviewDiffGroup[]
  hasChanges: boolean
}

const EM_DASH = '\u2014'

function nz(s: string | undefined | null): string {
  const t = (s ?? '').trim()
  return t.length > 0 ? t : EM_DASH
}

function formatApproval(a: {
  name?: string
  title?: string
  signature?: string
}): string {
  const name = (a.name ?? '').trim()
  const title = (a.title ?? '').trim()
  const sig = a.signature?.trim() ? 'signed' : 'unsigned'
  if (!name && !title) return EM_DASH
  const person = name && title ? `${name} (${title})` : name || title
  return `${person} — signature: ${sig}`
}

function diffIdentifierFields(
  before: WpsEditableSnapshot['identifiers'],
  after: WpsEditableSnapshot['identifiers'],
  keys: Array<[string, keyof WpsEditableSnapshot['identifiers']]>
): WpsPreviewDiffRow[] {
  const rows: WpsPreviewDiffRow[] = []
  for (const [label, key] of keys) {
    const b = (before[key] ?? '').toString()
    const a = (after[key] ?? '').toString()
    if (b !== a) {
      rows.push({ field: label, before: nz(b), after: nz(a) })
    }
  }
  return rows
}

function diffIdentifiers(
  before: WpsEditableSnapshot['identifiers'],
  after: WpsEditableSnapshot['identifiers']
): WpsPreviewDiffRow[] {
  const fields: Array<[string, keyof WpsEditableSnapshot['identifiers']]> = [
    ['WPS No.', 'wpsNumber'],
    ['Rev.', 'revision'],
    ['Application', 'application'],
    ['Equipment', 'equipmentDescription']
  ]
  return diffIdentifierFields(before, after, fields)
}

function diffIdentifiersSignificant(
  before: WpsEditableSnapshot['identifiers'],
  after: WpsEditableSnapshot['identifiers']
): WpsPreviewDiffRow[] {
  const fields: Array<[string, keyof WpsEditableSnapshot['identifiers']]> = [
    ['Application', 'application'],
    ['Equipment', 'equipmentDescription']
  ]
  return diffIdentifierFields(before, after, fields)
}

function diffApprovals(
  before: WpsEditableSnapshot['approvals'],
  after: WpsEditableSnapshot['approvals']
): WpsPreviewDiffRow[] {
  const byIdBefore = new Map(before.map((a) => [a.id, a]))
  const rows: WpsPreviewDiffRow[] = []
  for (const a of after) {
    const b = byIdBefore.get(a.id) ?? {
      id: a.id,
      label: a.label,
      name: '',
      title: '',
      date: ''
    }
    const beforePerson = formatApproval(b)
    const afterPerson = formatApproval(a)
    if (beforePerson !== afterPerson) {
      rows.push({
        field: `${a.label}: Signature`,
        before: beforePerson,
        after: afterPerson
      })
    }
    const beforeDate = nz(b.date)
    const afterDate = nz(a.date)
    if (beforeDate !== afterDate) {
      rows.push({
        field: `${a.label}: Date`,
        before: beforeDate,
        after: afterDate
      })
    }
    const beforeSig = b.signature?.trim() ? 'signed' : 'unsigned'
    const afterSig = a.signature?.trim() ? 'signed' : 'unsigned'
    if (beforeSig !== afterSig) {
      rows.push({
        field: `${a.label}: Digital signature`,
        before: beforeSig,
        after: afterSig
      })
    }
  }
  return rows
}

function diffNotes(
  before: WpsEditableSnapshot['notes'],
  after: WpsEditableSnapshot['notes']
): WpsPreviewDiffRow[] {
  const byIdBefore = new Map(before.map((n) => [n.id, n.body ?? '']))
  const rows: WpsPreviewDiffRow[] = []
  after.forEach((n, idx) => {
    const b = byIdBefore.get(n.id) ?? ''
    const a = n.body ?? ''
    if (b !== a) {
      rows.push({
        field: `Note ${idx + 1}`,
        before: nz(b),
        after: nz(a)
      })
    }
  })
  return rows
}

function buildWpsPreviewDiffFromParts(
  baseline: WpsEditableSnapshot,
  staged: WpsEditableSnapshot,
  includeMinorIdentifiers: boolean
): WpsPreviewDiff {
  const groups: WpsPreviewDiffGroup[] = []
  const idRows = includeMinorIdentifiers
    ? diffIdentifiers(baseline.identifiers, staged.identifiers)
    : diffIdentifiersSignificant(baseline.identifiers, staged.identifiers)
  if (idRows.length > 0) groups.push({ group: 'Identifiers', rows: idRows })
  const approvalRows = diffApprovals(baseline.approvals, staged.approvals)
  if (approvalRows.length > 0) groups.push({ group: 'Signatures', rows: approvalRows })
  const noteRows = diffNotes(baseline.notes, staged.notes)
  if (noteRows.length > 0) groups.push({ group: 'Notes', rows: noteRows })
  return { groups, hasChanges: groups.length > 0 }
}

export function buildWpsPreviewDiff(
  baseline: WpsEditableSnapshot,
  staged: WpsEditableSnapshot
): WpsPreviewDiff {
  return buildWpsPreviewDiffFromParts(baseline, staged, true)
}

/** Diff for confirm / discard UI — omits WPS No. and Rev. identifier rows. */
export function buildWpsPreviewSignificantDiff(
  baseline: WpsEditableSnapshot,
  staged: WpsEditableSnapshot
): WpsPreviewDiff {
  return buildWpsPreviewDiffFromParts(baseline, staged, false)
}

export function hasSignificantWpsPreviewChanges(
  baseline: WpsEditableSnapshot,
  staged: WpsEditableSnapshot
): boolean {
  return buildWpsPreviewSignificantDiff(baseline, staged).hasChanges
}
